/**
 * BusinessInfoStep Component
 *
 * Third step: Collect business information.
 * - Legal business name
 * - Tax filing name (DBA if applicable)
 * - Business phone
 * - Business address (only if different from personal address)
 */

import { useEffect, useRef, useState } from 'react';
import type { IMerchant, IOnboardingApplication } from '@order-up/models';
import { useAuth, useGetUserByUid } from '@order-up/client/vite';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { useSaveDraft } from '../../lib/hooks';
import { formatPhone, PHONE_MAX_LENGTH, stripNonDigits } from '../../lib/formatters';
import { US_STATES, type OnboardingApplicationData } from '../../types/onboarding';

export interface BusinessInfoStepProps {
  application: IOnboardingApplication;
}

/** Check if the saved business address differs from the owner's personal address. */
function hasDistinctBusinessAddress(appData: OnboardingApplicationData): boolean {
  const biz = appData.demographic?.businessAddress;
  const personal = appData.ownership?.owner?.ownerAddress;
  if (!biz || !personal) return false;
  // If business address has values and they differ from personal
  if (!biz.address1 && !biz.city && !biz.stateCd && !biz.zip) return false;
  return (
    biz.address1 !== personal.address1 ||
    biz.address2 !== personal.address2 ||
    biz.city !== personal.city ||
    biz.stateCd !== personal.stateCd ||
    biz.zip !== personal.zip
  );
}

/** Check if the saved business phone differs from the owner's personal phone. */
function hasDistinctBusinessPhone(
  savedBusinessPhone: string | undefined,
  personalPhoneDigits: string,
): boolean {
  const bizDigits = stripNonDigits(savedBusinessPhone || '');
  if (!bizDigits) return false;
  if (!personalPhoneDigits) return true;
  return bizDigits !== personalPhoneDigits;
}

export function BusinessInfoStep({ application }: BusinessInfoStepProps) {
  const { mutate: saveDraft } = useSaveDraft();

  // Pull the Firestore user doc to read the personal phone collected at signup.
  // Same convention as PersonalInfoStep — the auth listener invalidates this
  // cache, so it stays in sync without a second fetch.
  const { data: authData } = useAuth();
  const { data: firestoreUser } = useGetUserByUid(authData?.authUser?.uid);
  const personalPhoneDigits = firestoreUser?.phoneNumber?.number || '';
  const personalPhoneFormatted = personalPhoneDigits ? formatPhone(personalPhoneDigits) : '';
  const hasPersonalPhone = personalPhoneDigits.length > 0;

  const appData = application.applicationData as OnboardingApplicationData;
  const isSoleProp = appData.ownership?.ownershipTypeCd === 'INDIVSOLE';
  const ownerAddress = appData.ownership?.owner?.ownerAddress;

  // Local state for form fields
  const [formData, setFormData] = useState({
    legalBusinessName: appData.legalBusinessName || '',
    taxFilingName: appData.taxFilingName || '',
    businessPhone: formatPhone(appData.demographic?.businessPhone || ''),
    businessAddress: {
      address1: appData.demographic?.businessAddress?.address1 || '',
      address2: appData.demographic?.businessAddress?.address2 || '',
      city: appData.demographic?.businessAddress?.city || '',
      stateCd: appData.demographic?.businessAddress?.stateCd || '',
      zip: appData.demographic?.businessAddress?.zip || '',
    },
    // ISO-3166 alpha-2 country code stored on the OnboardingApplicationData
    // extension (CoPilot's `IAddress` doesn't carry country). Defaults to
    // 'US' on a fresh draft.
    businessAddressCountry: appData.businessAddressCountry || 'US',
  });

  // Track whether the user wants a different business address
  const [isDifferentAddress, setIsDifferentAddress] = useState(() =>
    hasDistinctBusinessAddress(appData)
  );

  // Track whether the user wants a different business phone. Defaults to
  // "different" if the saved phone is non-empty and doesn't match personal,
  // matching the address pattern. If no personal phone exists on the user
  // doc, this is forced to `true` (the input is the only path to a value).
  const [isDifferentPhone, setIsDifferentPhone] = useState(() =>
    hasDistinctBusinessPhone(appData.demographic?.businessPhone, personalPhoneDigits) ||
    !personalPhoneDigits
  );

  // The Firestore user doc may load asynchronously after first render. When
  // that happens AFTER `isDifferentPhone` has already initialized (forced to
  // `true` because no personal phone was known yet), reconcile once: if the
  // user hasn't typed a business phone, default to "use personal phone".
  // Mirrors PersonalInfoStep's seed-once-on-firestore-arrival pattern.
  const hasSeededPhoneFromFirestoreUser = useRef(false);
  useEffect(() => {
    if (hasSeededPhoneFromFirestoreUser.current) return;
    if (!firestoreUser) return;
    const personalDigits = firestoreUser.phoneNumber?.number || '';
    if (!personalDigits) {
      hasSeededPhoneFromFirestoreUser.current = true;
      return;
    }
    const bizDigits = stripNonDigits(formData.businessPhone);
    // If the user already typed a different number, leave the toggle alone.
    if (bizDigits && bizDigits !== personalDigits) {
      hasSeededPhoneFromFirestoreUser.current = true;
      return;
    }
    // Either the field is empty or it already matches — flip to "use personal".
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsDifferentPhone(false);
    hasSeededPhoneFromFirestoreUser.current = true;
  }, [firestoreUser, formData.businessPhone]);

  // Resolve the effective business address for saving
  const getEffectiveAddress = (different: boolean, addr: typeof formData.businessAddress) => {
    if (!different && ownerAddress) {
      return {
        address1: ownerAddress.address1 || '',
        address2: ownerAddress.address2 || '',
        city: ownerAddress.city || '',
        stateCd: ownerAddress.stateCd || '',
        zip: ownerAddress.zip || '',
      };
    }
    return addr;
  };

  // Resolve the effective business phone for saving — formatted (matches the
  // existing `businessPhone` storage convention).
  const getEffectivePhone = (different: boolean, formattedPhone: string) => {
    if (!different && personalPhoneFormatted) return personalPhoneFormatted;
    return formattedPhone;
  };

  const handleChange = (field: string, rawValue: string) => {
    const updatedFormData = { ...formData };

    if (field.startsWith('businessAddress.')) {
      const addressField = field.split('.')[1];
      updatedFormData.businessAddress = {
        ...updatedFormData.businessAddress,
        [addressField]: rawValue,
      };
    } else {
      switch (field) {
        case 'legalBusinessName':
          updatedFormData.legalBusinessName = rawValue;
          break;
        case 'taxFilingName':
          updatedFormData.taxFilingName = rawValue;
          break;
        case 'businessPhone':
          updatedFormData.businessPhone = formatPhone(rawValue);
          break;
        case 'businessAddressCountry':
          updatedFormData.businessAddressCountry = rawValue;
          break;
      }
    }

    setFormData(updatedFormData);

    const effectiveAddress = getEffectiveAddress(isDifferentAddress, updatedFormData.businessAddress);
    const effectivePhone = getEffectivePhone(isDifferentPhone, updatedFormData.businessPhone);

    const updates: Partial<OnboardingApplicationData> = {
      legalBusinessName: updatedFormData.legalBusinessName,
      taxFilingName: updatedFormData.taxFilingName,
      demographic: {
        ...appData.demographic,
        businessPhone: effectivePhone,
        businessAddress: effectiveAddress,
      },
      businessAddressCountry: updatedFormData.businessAddressCountry,
    };

    saveDraft({
      id: application.id,
      data: updates as Partial<IMerchant>,
      step: application.currentStep,
    });
  };

  // Toggle the "different address" checkbox
  const handleAddressToggle = (checked: boolean) => {
    setIsDifferentAddress(checked);

    // Save immediately with the resolved address
    const effectiveAddress = getEffectiveAddress(checked, formData.businessAddress);
    const effectivePhone = getEffectivePhone(isDifferentPhone, formData.businessPhone);

    const updates: Partial<OnboardingApplicationData> = {
      legalBusinessName: formData.legalBusinessName,
      taxFilingName: formData.taxFilingName,
      demographic: {
        ...appData.demographic,
        businessPhone: effectivePhone,
        businessAddress: effectiveAddress,
      },
    };

    saveDraft({
      id: application.id,
      data: updates as Partial<IMerchant>,
      step: application.currentStep,
    });
  };

  // Toggle the "use personal phone" checkbox. `checked` here represents
  // "different than personal" to mirror the address toggle's semantics.
  const handlePhoneToggle = (different: boolean) => {
    setIsDifferentPhone(different);

    const effectiveAddress = getEffectiveAddress(isDifferentAddress, formData.businessAddress);
    const effectivePhone = getEffectivePhone(different, formData.businessPhone);

    const updates: Partial<OnboardingApplicationData> = {
      legalBusinessName: formData.legalBusinessName,
      taxFilingName: formData.taxFilingName,
      demographic: {
        ...appData.demographic,
        businessPhone: effectivePhone,
        businessAddress: effectiveAddress,
      },
    };

    saveDraft({
      id: application.id,
      data: updates as Partial<IMerchant>,
      step: application.currentStep,
    });
  };

  return (
    <div className="space-y-6">
      {/* Legal Business Name */}
      <Input
        label="Legal Business Name"
        type="text"
        value={formData.legalBusinessName}
        onChange={(e) => handleChange('legalBusinessName', e.target.value)}
        placeholder={isSoleProp ? "John Doe or John Doe's Restaurant" : 'ABC Restaurant LLC'}
        required
      />

      {/* Tax Filing Name / DBA */}
      <Input
        label="Tax Filing Name / DBA (if different)"
        type="text"
        value={formData.taxFilingName}
        onChange={(e) => handleChange('taxFilingName', e.target.value)}
        placeholder="Doing Business As (optional)"
      />

      {/* Business Phone */}
      <div className="space-y-3">
        {isDifferentPhone ? (
          <Input
            label="Business Phone Number"
            type="tel"
            value={formData.businessPhone}
            onChange={(e) => handleChange('businessPhone', e.target.value)}
            placeholder="(555) 123-4567"
            required
            maxLength={PHONE_MAX_LENGTH}
            inputMode="tel"
            autoComplete="tel"
          />
        ) : (
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-zinc-300">
              Business Phone Number
            </span>
            <div className="rounded-lg border border-border/50 bg-zinc-900/30 p-4">
              <p className="text-sm text-zinc-400">
                Your personal phone number{personalPhoneFormatted ? ` (${personalPhoneFormatted})` : ''}{' '}
                will be used as the business phone.
              </p>
            </div>
          </div>
        )}

        {/* Checkbox: only offered when a personal phone exists on the user doc. */}
        {hasPersonalPhone && (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isDifferentPhone}
              onChange={(e) => handlePhoneToggle(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500/20 focus:ring-2"
            />
            <span className="text-sm text-zinc-300">
              Is Business phone different than Personal Phone?
            </span>
          </label>
        )}
      </div>

      {/* Business Address Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">Business Address</h3>

        {/* Checkbox: different from personal address? */}
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isDifferentAddress}
            onChange={(e) => handleAddressToggle(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-blue-500 focus:ring-blue-500/20 focus:ring-2"
          />
          <span className="text-sm text-zinc-300">
            Is Business address different than Personal Address?
          </span>
        </label>

        {isDifferentAddress ? (
          <>
            <Input
              label="Address"
              type="text"
              value={formData.businessAddress.address1}
              onChange={(e) => handleChange('businessAddress.address1', e.target.value)}
              placeholder="123 Main St"
              required
            />

            <Input
              label="Apt, Suite, Unit, Floor (Optional)"
              type="text"
              value={formData.businessAddress.address2}
              onChange={(e) => handleChange('businessAddress.address2', e.target.value)}
              placeholder="Suite 200"
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="City"
                type="text"
                value={formData.businessAddress.city}
                onChange={(e) => handleChange('businessAddress.city', e.target.value)}
                placeholder="San Francisco"
                required
              />

              <Select
                label="State"
                value={formData.businessAddress.stateCd}
                onChange={(e) => handleChange('businessAddress.stateCd', e.target.value)}
                required
              >
                <option value="">Select State</option>
                {US_STATES.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="ZIP Code"
                type="text"
                value={formData.businessAddress.zip}
                onChange={(e) => handleChange('businessAddress.zip', e.target.value)}
                placeholder="94102"
                required
                maxLength={10}
              />

              <Select
                label="Country"
                value={formData.businessAddressCountry}
                onChange={(e) => handleChange('businessAddressCountry', e.target.value)}
                required
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </Select>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-border/50 bg-zinc-900/30 p-4">
            <p className="text-sm text-zinc-400">
              Your personal address will be used as the business address.
            </p>
          </div>
        )}
      </div>

      {/* Info callout */}
      <div className="rounded-lg border border-border/50 bg-zinc-900/30 p-4">
        <p className="text-sm text-zinc-300">
          <span className="font-medium text-white">Address Note:</span>
          {' '}Use your primary business location or headquarters. This address will be used for
          official correspondence and verification purposes.
        </p>
      </div>
    </div>
  );
}
